import type { Band } from '../entropy/score';
import type { RunResult } from '../run';
import { h } from './dom';
import { APP_HOST, contributorPhrases } from './export';
import { BAND_TEXT, BAND_TITLE } from './summary';

/**
 * Shareable, redacted summary image. It deliberately contains only the exposure band and the top
 * contributors: no hash (that *is* an identifier), no bit count, and no raw values.
 */
export interface ShareCardContent {
  band: Band;
  title: string;
  text: string;
  contributors: string[];
  date: string;
  site: string;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function shareCardContent(result: RunResult): ShareCardContent {
  const band = result.score.band;
  return {
    band,
    title: BAND_TITLE[band],
    text: BAND_TEXT[band],
    contributors: contributorPhrases(result).map(capitalize),
    date: result.startedAt.slice(0, 10),
    site: APP_HOST,
  };
}

export const CARD_WIDTH = 1200;
export const CARD_HEIGHT = 630;

const COLORS = {
  bg: '#111315',
  text: '#e7e9eb',
  muted: '#a3aab1',
  pill: '#212529',
  accent: '#2dd4bf',
  band: { low: '#2dd4bf', moderate: '#f2b33d', high: '#f472b6' } satisfies Record<Band, string>,
};
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines) break;
    } else {
      line = next;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  return lines;
}

function pill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, hgt: number) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, hgt, hgt / 2);
  else ctx.rect(x, y, w, hgt);
  ctx.fill();
}

export function drawShareCard(content: ShareCardContent): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  // Read back once for the blocked-readback check; tell the browser so it keeps the canvas CPU-side.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const left = 88;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
  ctx.fillStyle = COLORS.band[content.band];
  ctx.fillRect(0, 0, 14, CARD_HEIGHT);

  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.accent;
  ctx.font = `700 26px ${FONT}`;
  ctx.fillText('FINGERPRINT SELF-CHECK', left, 92);

  ctx.fillStyle = COLORS.band[content.band];
  ctx.font = `800 88px ${FONT}`;
  ctx.fillText(content.title, left, 200);

  ctx.fillStyle = COLORS.text;
  ctx.font = `400 30px ${FONT}`;
  let y = 262;
  for (const line of wrap(ctx, content.text, CARD_WIDTH - left * 2, 3)) {
    ctx.fillText(line, left, y);
    y += 42;
  }

  if (content.contributors.length) {
    y += 34;
    ctx.fillStyle = COLORS.muted;
    ctx.font = `700 22px ${FONT}`;
    ctx.fillText('TOP CONTRIBUTORS', left, y);
    y += 22;
    ctx.font = `600 28px ${FONT}`;
    let x = left;
    for (const phrase of content.contributors) {
      const w = ctx.measureText(phrase).width + 44;
      if (x + w > CARD_WIDTH - left) break;
      ctx.fillStyle = COLORS.pill;
      pill(ctx, x, y, w, 52);
      ctx.fillStyle = COLORS.text;
      ctx.fillText(phrase, x + 22, y + 36);
      x += w + 14;
    }
  }

  ctx.fillStyle = COLORS.muted;
  ctx.font = `400 24px ${FONT}`;
  ctx.fillText(`${content.site} · runs entirely in your browser`, left, CARD_HEIGHT - 48);
  ctx.textAlign = 'right';
  ctx.fillText(content.date, CARD_WIDTH - left, CARD_HEIGHT - 48);
  return canvas;
}

/**
 * Anti-fingerprinting browsers may blank or garble canvas readback. Sample background-only points;
 * small farbling noise is tolerated, a blank or replaced image is not.
 */
export function readbackLooksBlocked(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  const points: Array<[number, number]> = [
    [600, 20],
    [1150, 40],
    [1150, 300],
    [700, 470],
    [40, 610],
    [1150, 520],
  ];
  const bg = [0x11, 0x13, 0x15];
  let bad = 0;
  try {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (const [x, y] of points) {
      const o = (y * canvas.width + x) * 4;
      if (bg.some((c, i) => Math.abs((data[o + i] ?? 0) - c) > 8)) bad++;
    }
  } catch {
    return true;
  }
  return bad > points.length / 2;
}

export function renderShareControls(result: RunResult): HTMLElement {
  const content = shareCardContent(result);
  const status = h('p', { class: 'export-status', role: 'status' });
  const alt = `Share image: ${content.title}. Top contributors: ${content.contributors.join(', ') || 'none'}.`;
  const figure = h('figure', { class: 'share-figure', hidden: true });
  const download = h('button', { type: 'button', class: 'button', hidden: true }, 'Download image');
  const create = h('button', { type: 'button', class: 'button' }, 'Create share image');
  let canvas: HTMLCanvasElement | null = null;

  create.addEventListener('click', () => {
    canvas = drawShareCard(content);
    if (!canvas) {
      status.textContent = 'Your browser does not allow drawing on a canvas here.';
      return;
    }
    if (readbackLooksBlocked(canvas)) {
      status.textContent = 'Your browser blocks reading canvas images back, so the image can’t be exported. That is one of its fingerprinting protections working.';
      return;
    }
    // Show the canvas itself rather than a data: URL image, so the preview never appears as a
    // request in DevTools' Network panel.
    canvas.className = 'share-preview';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', alt);
    figure.replaceChildren(canvas);
    figure.hidden = false;
    download.hidden = false;
    create.textContent = 'Recreate image';
    status.textContent = 'Preview ready. Nothing has been shared yet.';
  });

  download.addEventListener('click', () => {
    canvas?.toBlob((blob) => {
      if (!blob) {
        status.textContent = 'Could not create the image file.';
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = h('a', { href: url, download: `fingerprint-summary-${content.date}.png` });
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
      status.textContent = 'Saved to your downloads.';
    }, 'image/png');
  });

  return h(
    'div',
    { class: 'share' },
    h('h3', null, 'Share a redacted summary'),
    h(
      'p',
      null,
      'Creates an image with only your exposure band and top contributors: no fingerprint hash, no bit count and no raw values. It is drawn on this page and saved to your device; sharing it is up to you.',
    ),
    h('div', { class: 'actions' }, create, download),
    status,
    figure,
  );
}
